/**
 * Translation system — React context + hook for runtime i18n.
 *
 * - Static translations embedded in bundle — loads INSTANTLY, zero network dependency
 * - Backend fetch runs in background to refresh/update, not as a hard requirement
 * - Falls back to English (BASE_UI_STRINGS) when key not found
 * - Auto-detects browser language on first visit (handles both hyphen and underscore formats)
 * - Syncs language preference with backend (UserProfile.language) when authenticated
 *
 * Usage:
 *   const { t, language, setLanguage, loading } = useTranslation()
 *   <h1>{t('dashboard.welcome')}</h1>
 */

import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef, useMemo,
} from 'react'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'

// ── Static translation imports (embedded in bundle) ──────────────
import enStatic from './translations/en.json'
import esStatic from './translations/es.json'
import ptStatic from './translations/pt.json'
import frStatic from './translations/fr.json'
import deStatic from './translations/de.json'
import itStatic from './translations/it.json'
import jaStatic from './translations/ja.json'
import zhStatic from './translations/zh.json'
import koStatic from './translations/ko.json'
import ruStatic from './translations/ru.json'
import arStatic from './translations/ar.json'
import hiStatic from './translations/hi.json'

const STATIC_TRANSLATIONS: Record<string, Record<string, string>> = {
  en: enStatic,
  es: esStatic,
  pt: ptStatic,
  fr: frStatic,
  de: deStatic,
  it: itStatic,
  ja: jaStatic,
  zh: zhStatic,
  ko: koStatic,
  ru: ruStatic,
  ar: arStatic,
  hi: hiStatic,
}

// ── Types ────────────────────────────────────────────────────────

interface TranslationContextValue {
  t: (key: string, fallback?: string) => string
  language: string
  setLanguage: (code: string) => Promise<void>
  loading: boolean
  ready: boolean
  supportedLanguages: { code: string; name: string; flag: string }[]
}

const FLAGS: Record<string, string> = {
  en: '🇺🇸', es: '🇪🇸', pt: '🇧🇷', fr: '🇫🇷', de: '🇩🇪', it: '🇮🇹',
  ja: '🇯🇵', zh: '🇨🇳', ko: '🇰🇷', ru: '🇷🇺', ar: '🇸🇦', hi: '🇮🇳',
}

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English',    flag: FLAGS.en },
  { code: 'es', name: 'Español',    flag: FLAGS.es },
  { code: 'pt', name: 'Português',  flag: FLAGS.pt },
  { code: 'fr', name: 'Français',   flag: FLAGS.fr },
  { code: 'de', name: 'Deutsch',    flag: FLAGS.de },
  { code: 'it', name: 'Italiano',   flag: FLAGS.it },
  { code: 'ja', name: '日本語',      flag: FLAGS.ja },
  { code: 'zh', name: '中文',        flag: FLAGS.zh },
  { code: 'ko', name: '한국어',      flag: FLAGS.ko },
  { code: 'ru', name: 'Русский',    flag: FLAGS.ru },
  { code: 'ar', name: 'العربية',    flag: FLAGS.ar },
  { code: 'hi', name: 'हिन्दी',      flag: FLAGS.hi },
]

const STORAGE_KEY = 'selah_preferred_language'
const TRANSLATION_CACHE_KEY = 'selah_translations_cache'

// ── Context ──────────────────────────────────────────────────────

const TranslationContext = createContext<TranslationContextValue>({
  t: (key, fb) => fb ?? key,
  language: 'en',
  setLanguage: async () => {},
  loading: false,
  ready: false,
  supportedLanguages: SUPPORTED_LANGUAGES,
})

// ── Helpers ──────────────────────────────────────────────────────

/** Safe localStorage get — never throws, even in private browsing. */
function safeGetItem(key: string): string | null {
  if (typeof window === 'undefined') return null
  try { return localStorage.getItem(key) } catch { return null }
}

/** Safe localStorage set — never throws. */
function safeSetItem(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try { localStorage.setItem(key, value) } catch { /* storage full or private */ }
}

/** Safe localStorage remove — never throws. */
function safeRemoveItem(key: string): void {
  if (typeof window === 'undefined') return
  try { localStorage.removeItem(key) } catch {}
}

function getStoredLanguage(): string | null {
  return safeGetItem(STORAGE_KEY)
}

function storeLanguage(code: string): void {
  safeSetItem(STORAGE_KEY, code)
}

/**
 * Detect browser language from navigator.
 * Handles BOTH hyphen (en-US) and underscore (pt_BR) formats.
 * Falls back to navigator.languages (plural) array if singular is unreliable.
 */
function getBrowserLanguage(): string {
  if (typeof navigator === 'undefined') return 'en'

  // Try navigator.language first (standard BCP 47, but may use underscores on Android WebView)
  const raw = navigator.language || ''
  const code = langTagToCode(raw)
  if (code && code !== 'en') return code

  // Fallback: navigator.languages (ordered preference array)
  if (navigator.languages && navigator.languages.length > 0) {
    for (const lang of navigator.languages) {
      const c = langTagToCode(lang)
      if (c && c !== 'en') return c
    }
  }

  return 'en'
}

/** Map a language tag (BCP 47 or POSIX) to our supported 2-letter code. */
function langTagToCode(tag: string): string | null {
  if (!tag) return null
  const normalized = tag.toLowerCase()
  // Direct match
  if (BROWSER_TO_CODE[normalized]) return BROWSER_TO_CODE[normalized]
  // Split on BOTH hyphens and underscores (Android PWA fix)
  const parts = normalized.split(/[-_]/)
  const primary = parts[0]
  if (BROWSER_TO_CODE[primary]) return BROWSER_TO_CODE[primary]
  return null
}

/** Map common browser language tags to our supported codes. */
const BROWSER_TO_CODE: Record<string, string> = {
  'en': 'en', 'en-us': 'en', 'en-gb': 'en', 'en-au': 'en', 'en-ca': 'en',
  'en-in': 'en', 'en-za': 'en', 'en-nz': 'en', 'en-ie': 'en',
  'es': 'es', 'es-es': 'es', 'es-mx': 'es', 'es-ar': 'es', 'es-co': 'es', 'es-cl': 'es',
  'pt': 'pt', 'pt-br': 'pt', 'pt-pt': 'pt',
  'pt_br': 'pt', 'pt_pt': 'pt',  // Android WebView underscore variants
  'es_mx': 'es', 'es_es': 'es', 'es_ar': 'es',  // Android WebView underscore variants
  'fr': 'fr', 'fr-fr': 'fr', 'fr-ca': 'fr', 'fr-be': 'fr', 'fr-ch': 'fr',
  'de': 'de', 'de-de': 'de', 'de-at': 'de', 'de-ch': 'de', 'de-li': 'de',
  'it': 'it', 'it-it': 'it', 'it-ch': 'it',
  'ja': 'ja', 'ja-jp': 'ja',
  'zh': 'zh', 'zh-cn': 'zh', 'zh-tw': 'zh', 'zh-hk': 'zh',
  'zh-hans': 'zh', 'zh-hans-cn': 'zh', 'zh-hant': 'zh', 'zh-hant-tw': 'zh',
  'ko': 'ko', 'ko-kr': 'ko',
  'ru': 'ru', 'ru-ru': 'ru',
  'ar': 'ar', 'ar-sa': 'ar', 'ar-eg': 'ar',
  'hi': 'hi', 'hi-in': 'hi',
}

/**
 * Get cached translations (backend-sourced updates, not the bundle static ones).
 * Stored as a single JSON blob keyed by `TRANSLATION_CACHE_KEY`.
 */
function getCachedTranslations(): Record<string, Record<string, string>> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = safeGetItem(TRANSLATION_CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

function setCachedTranslations(cache: Record<string, Record<string, string>>): void {
  if (typeof window === 'undefined') return
  try {
    safeSetItem(TRANSLATION_CACHE_KEY, JSON.stringify(cache))
  } catch {}
}

// ── Base UI strings that we always want translated ───────────────

const BASE_UI_STRINGS: Record<string, string> = {
  // ── Navigation (all role tabs) ──────────────────────────────
  'nav.home': 'Home',
  'nav.lessons': 'Lessons',
  'nav.attendance': 'Attendance',
  'nav.scheduling': 'Scheduling',
  'nav.students': 'Students',
  'nav.teachers': 'Teachers',
  'nav.billing': 'Billing',
  'nav.profile': 'Profile',
  'nav.settings': 'Settings',
  'nav.practice': 'Practice',
  'nav.ai_tutor': 'AI Tutor',
  'nav.tools': 'Tools',
  'nav.midi': 'MIDI',
  'nav.metronome': 'Metronome',
  'nav.price_calculator': 'Price Calculator',
  'nav.my_students': 'My Students',
  'nav.reports': 'Reports',
  'nav.earnings': 'Earnings',
  'nav.organizations': 'Organizations',
  'nav.audit': 'Audit Log',
  'nav.finance': 'Finance',
  'nav.users': 'Users',
  'nav.communications': 'Communications',
  'nav.ligio': 'Ligio',
  'nav.platform': 'Platform',
  'nav.overview': 'Overview',
  'nav.people': 'People',
  'nav.operations': 'Operations',
  'nav.admin': 'Admin',
  'nav.studio': 'Studio',
  'nav.content_studio': 'Content Studio',
  'nav.schedule_earnings': 'Schedule & Earnings',
  'nav.you': 'You',
  'nav.progress': 'Progress',
  'nav.security': 'Security',
  'nav.comms': 'Comms',

  // ── Role labels ─────────────────────────────────────────────
  'role.super_admin': 'Super Admin',
  'role.admin': 'Administrator',
  'role.teacher': 'Teacher',
  'role.student': 'Student',
  'role.guardian': 'Guardian',
  'role.director': 'Director',

  // ── Common actions ──────────────────────────────────────────
  'btn.save': 'Save Changes',
  'btn.cancel': 'Cancel',
  'btn.edit': 'Edit',
  'btn.delete': 'Delete',
  'btn.create': 'Create',
  'btn.search': 'Search',
  'btn.filter': 'Filter',
  'btn.export': 'Export',
  'btn.import': 'Import',
  'btn.send': 'Send',
  'btn.close': 'Close',
  'btn.back': 'Back',
  'btn.next': 'Next',
  'btn.confirm': 'Confirm',
  'btn.retry': 'Retry',
  'btn.load_more': 'Load More',
  'btn.sign_out': 'Sign Out',
  'btn.upload': 'Upload',
  'btn.download': 'Download',
  'btn.view': 'View',
  'btn.view_all': 'View All',
  'btn.approve': 'Approve',
  'btn.reject': 'Reject',
  'btn.archive': 'Archive',
  'btn.activate': 'Activate',
  'btn.deactivate': 'Deactivate',
  'btn.dismiss': 'Dismiss',
  'btn.clear': 'Clear',
  'btn.reset': 'Reset',
  'btn.refresh': 'Refresh',
  'btn.print': 'Print',
  'btn.log_in': 'Log In',
  'btn.sign_up': 'Sign Up',
  'btn.forgot_password': 'Forgot Password?',
  'btn.reset_password': 'Reset Password',
  'btn.verify_email': 'Verify Email',
  'btn.resend': 'Resend',

  // ── Common labels ───────────────────────────────────────────
  'label.loading': 'Loading…',
  'label.error': 'Error',
  'label.no_results': 'No results found',
  'label.no_data': 'No data available',
  'label.confirm_delete': 'Are you sure you want to delete this?',
  'label.search_placeholder': 'Search…',
  'label.select_all': 'Select All',
  'label.none': 'None',
  'label.all': 'All',
  'label.enabled': 'Enabled',
  'label.disabled': 'Disabled',
  'label.active': 'Active',
  'label.inactive': 'Inactive',
  'label.required': 'Required',
  'label.optional': 'Optional',
  'label.status': 'Status',
  'label.actions': 'Actions',
  'label.name': 'Name',
  'label.email': 'Email',
  'label.phone': 'Phone',
  'label.date': 'Date',
  'label.time': 'Time',
  'label.duration': 'Duration',
  'label.notes': 'Notes',
  'label.description': 'Description',
  'label.amount': 'Amount',
  'label.total': 'Total',
  'label.balance': 'Balance',
  'label.price': 'Price',
  'label.quantity': 'Quantity',
  'label.unit': 'Unit',
  'label.role': 'Role',
  'label.type': 'Type',
  'label.level': 'Level',
  'label.subject': 'Subject',
  'label.message': 'Message',
  'label.password': 'Password',
  'label.confirm_password': 'Confirm Password',
  'label.current_password': 'Current Password',
  'label.new_password': 'New Password',
  'label.remember_me': 'Remember Me',
  'label.first_name': 'First Name',
  'label.last_name': 'Last Name',
  'label.display_name': 'Display Name',
  'label.username': 'Username',
  'label.organization': 'Organization',
  'label.page': 'Page',
  'label.of': 'of',

  // ── Status labels ───────────────────────────────────────────
  'status.paid': 'Paid',
  'status.pending': 'Pending',
  'status.overdue': 'Overdue',
  'status.cancelled': 'Cancelled',
  'status.sent': 'Sent',
  'status.approved': 'Approved',
  'status.denied': 'Denied',
  'status.completed': 'Completed',
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.expired': 'Expired',
  'status.refunded': 'Refunded',
  'status.failed': 'Failed',
  'status.open': 'Open',

  // ── Dashboard ───────────────────────────────────────────────
  'dashboard.welcome': 'Welcome back',
  'dashboard.overview': 'Overview',
  'dashboard.quick_actions': 'Quick Actions',
  'dashboard.recent_activity': 'Recent Activity',
  'dashboard.upcoming_lessons': 'Upcoming Lessons',
  'dashboard.statistics': 'Statistics',
  'dashboard.health': 'System Health',
  'dashboard.alerts': 'Alerts',
  'dashboard.metrics': 'Metrics',
  'dashboard.command_center': 'Command Center',
  'dashboard.financial_command': 'Financial Command',
  'dashboard.platform_engineering': 'Platform Engineering',
  'dashboard.people_command': 'People Command',
  'dashboard.organization_command': 'Organization Command',
  'dashboard.today': 'Today',

  // ── Login / Auth ────────────────────────────────────────────
  'auth.welcome_back': 'Welcome back',
  'auth.sign_in': 'Sign in to your account',
  'auth.sign_in_heading': 'Welcome back',
  'auth.sign_in_subtitle': 'Sign in to your account',
  'auth.signing_in': 'Signing in…',
  'auth.sign_in_btn': 'Sign In',
  'auth.register': 'Register',
  'auth.forgot_password': 'Forgot Password?',
  'auth.no_account': "Don't have an account?",
  'auth.has_account': 'Already have an account?',
  'auth.create_account': 'Create an account',
  'auth.email_placeholder': 'you@example.com',
  'auth.password_placeholder': 'Enter your password',

  // ── Notifications ───────────────────────────────────────────
  'notifications.title': 'Notifications',
  'notifications.empty': 'No notifications',
  'notifications.mark_all_read': 'Mark all as read',

  // ── Language ────────────────────────────────────────────────
  'language.select': 'Select Language',
  'language.current': 'Current language',

  // ── Time ────────────────────────────────────────────────────
  'time.just_now': 'just now',
  'time.minutes_ago': '{n}m ago',
  'time.hours_ago': '{n}h ago',
  'time.days_ago': '{n}d ago',
  'time.months_ago': '{n}mo ago',
  'time.never': 'Never',

  // ── Calendar ────────────────────────────────────────────────
  'calendar.today': 'Today',
  'calendar.tomorrow': 'Tomorrow',
  'calendar.yesterday': 'Yesterday',
  'calendar.this_week': 'This Week',
  'calendar.next_week': 'Next Week',
  'calendar.this_month': 'This Month',
  'calendar.next_month': 'Next Month',
}

// ── Provider ─────────────────────────────────────────────────────

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()

  // ── Language detection (runs once, synchronously on mount) ─────
  const [language, setLanguageState] = useState<string>(() => {
    const stored = getStoredLanguage()
    if (stored) return stored
    const detected = getBrowserLanguage()
    storeLanguage(detected)
    return detected
  })

  // ── Translations state — starts with STATIC bundle (instant) ──
  const [translations, setTranslations] = useState<Record<string, string>>(() => {
    // English = identity map from BASE_UI_STRINGS; other langs from static JSON
    if (language === 'en') return {}
    return STATIC_TRANSLATIONS[language] || {}
  })

  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(true)  // true immediately — static bundle is ready
  const initializedRef = useRef(false)
  const bgFetchedRef = useRef(false)

  // ── When language changes, load static + optionally refresh ──
  useEffect(() => {
    // Switch to static translations immediately
    if (language === 'en') {
      setTranslations({})
      setReady(true)
      return
    }

    const staticMap = STATIC_TRANSLATIONS[language]
    if (staticMap && Object.keys(staticMap).length > 0) {
      setTranslations(staticMap)
      setReady(true)
    }

    // Background refresh: fetch from backend to get any newer translations
    // Only runs once per language per session (avoids redundant fetches)
    if (bgFetchedRef.current) return
    bgFetchedRef.current = true

    let cancelled = false
    const refreshFromBackend = async () => {
      setLoading(true)
      try {
        const resp = await api.post<{
          translations: Record<string, string>
          backend: string
        }>('/translations/ui-translations/', {
          target_language: language,
          strings: BASE_UI_STRINGS,
        })

        if (cancelled) return

        const map = resp.translations || {}
        if (Object.keys(map).length > 0) {
          // Merge with static — backend takes priority
          const merged = { ...staticMap, ...map }
          setTranslations(merged)

          // Update cache so next visit uses latest
          const cache = getCachedTranslations() || {}
          cache[language] = map
          setCachedTranslations(cache)
        }
      } catch (err) {
        console.debug('[i18n] Background refresh failed (using static):', (err as Error).message)
        // Static translations already loaded — no fallback needed
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    refreshFromBackend()
    return () => { cancelled = true }
  }, [language])

  // ── When user logs in, fetch saved language from backend ──────
  useEffect(() => {
    if (!user) return
    if (initializedRef.current) return
    initializedRef.current = true

    const fetchSavedLanguage = async () => {
      try {
        const profile = await api.get<{ language?: string }>('/accounts/profiles/me/')
        if (profile?.language && profile.language !== 'en') {
          const stored = getStoredLanguage()
          if (!stored || stored === 'en') {
            storeLanguage(profile.language)
            setLanguageState(profile.language)
          }
        }
      } catch {
        // Profile may not exist — that's fine
      }
    }
    fetchSavedLanguage()
  }, [user])

  // ── Lookup function ────────────────────────────────────────
  const t = useCallback((key: string, fallback?: string): string => {
    return translations[key] || fallback || BASE_UI_STRINGS[key] || key
  }, [translations])

  // ── Set language — persists to localStorage + backend ─────
  const setLanguage = useCallback(async (code: string) => {
    storeLanguage(code)
    setLanguageState(code)
    bgFetchedRef.current = false  // permit background refresh for new language

    // Only sync to backend if authenticated (avoids 401 nuking the session)
    if (!user) return
    try {
      await api.post('/translations/set-language/', { language: code })
    } catch {
      try {
        await api.patch('/accounts/profiles/me/', { language: code })
      } catch {
        // Best-effort — localStorage is sufficient
      }
    }
  }, [user])

  const ctxValue = useMemo(() => ({
    t, language, setLanguage, loading, ready,
    supportedLanguages: SUPPORTED_LANGUAGES,
  }), [t, language, setLanguage, loading, ready])

  return (
    <TranslationContext.Provider value={ctxValue}>
      {children}
    </TranslationContext.Provider>
  )
}

// ── Hook ────────────────────────────────────────────────────────

export function useTranslation(): TranslationContextValue {
  return useContext(TranslationContext)
}

/** Simple inline hook for components that only need t(). */
export function useT() {
  const { t, language, ready } = useTranslation()
  return { t, language, ready }
}

export { SUPPORTED_LANGUAGES, BASE_UI_STRINGS }
